// Keys the Display tab reads off a provider's report. Mirrors the defaulting in
// DisplayTabController.js:150-159, which applies `|| []` to every one of them —
// i.e. all are optional on a provider report and must be explicitly reset, not
// left to a spread. A blind `{ ...chartData, ...slice }` would let a slice that
// omits e.g. `byDay` inherit the AGGREGATE's rows, reproducing #11679 inside the
// chart's byDay and Device modes.
// `byDate` is handled separately (it needs per-row copies), so it is not listed
// here. Only byDay / deviceType / publishers / creatives are read off
// displayChartData today; the rest are reset defensively so that a future
// consumer reading them cannot silently pick up aggregate rows.
const SLICE_KEYS = [
  'byDay',
  'publishers',
  'creatives',
  'deviceType',
  'videoLogs',
  'segments',
  'lineItems',
];

/**
 * Pick the Display-tab data for the currently selected provider tab.
 *
 * `chartData` is page-level state shared across the Summary / Display /
 * Transactions / Sessions / Basket tabs, and DisplayTabController writes it only
 * once — its `!chartData.byDate` gate (DisplayTabController.js:106) closes after
 * the first run, while `providerIndex` is still 'All'. It therefore stays pinned
 * to the aggregate. Re-slicing here avoids un-freezing that shared state.
 *
 * Resolution rules:
 *   - 'All' / missing / unresolvable index → the aggregate (cannot tell which
 *     campaign is meant, so the aggregate is the honest answer).
 *   - A campaign that resolves but carries no data → EMPTY, never the aggregate.
 *     Falling back to the aggregate would render "All" figures under a named
 *     campaign's tab, which is the very defect this exists to fix.
 *
 * @param {object} chartData     Page-level chart state (the aggregated slice).
 * @param {Array}  providersTab  `chartData.displayReports` — `[dsp, report, meta]`.
 * @param {string} providerIndex 'All', or the stringified index of a provider.
 * @returns {object} The aggregate, or `chartData` with the provider's slice
 *                   applied over it.
 */
export const selectProviderSlice = (chartData, providersTab, providerIndex) => {
  if (!chartData) return chartData;
  if (providerIndex === 'All' || providerIndex === undefined || providerIndex === null)
    return chartData;

  const parsedIndex = parseInt(providerIndex, 10);
  if (Number.isNaN(parsedIndex)) return chartData;

  const selected = providersTab && providersTab[parsedIndex];
  if (!selected) return chartData;

  const slice = selected[1] || {};
  const sliced = { ...chartData };

  SLICE_KEYS.forEach((key) => {
    sliced[key] = Array.isArray(slice[key]) ? slice[key].slice() : [];
  });

  // byDate additionally gets per-row copies. `processPercentageDisplay`
  // (campaigndetails/utils/index.js:1094) rewrites `xAxis` IN PLACE on the array
  // it is handed, via a local-tz `moment()` — and CampaignDetailBar:112 hands it
  // `providersTab[i][1].byDate`, the array we would otherwise be aliasing into
  // the chart and KPI path. East of UTC that rewrite shifts a day backward
  // (`'2026-06-01'` → local midnight → 2026-05-31T16:00Z), breaching the locked
  // four-anchor UTC contract. Copy defensively rather than change the shared
  // util, which has other callers.
  sliced.byDate = Array.isArray(slice.byDate) ? slice.byDate.map((row) => ({ ...row })) : [];

  // `chartsBuilt` is a REPORT-level generation timestamp, not a per-campaign
  // field, and drives the chart's "Updated <time>" stamp plus the creative
  // activity window in OverviewTab. Fall back to the report-level value so the
  // stamp does not vanish on every provider tab.
  sliced.chartsBuilt = slice.chartsBuilt !== undefined ? slice.chartsBuilt : chartData.chartsBuilt;

  // The Campaigns card lists every campaign in the order — an aggregate-only
  // view. DisplayTabController:161-164 forces it empty for a selected provider;
  // match that so one campaign's tab does not list all of them.
  sliced.campaigns = [];

  return sliced;
};
